import React, { useState, FC, useRef, RefObject, useEffect, useMemo } from 'react';
import {
    Button,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert, AlertTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TimekeeperService } from 'services/TimekeeperService';
import {
    LimitFormValues, defaultLimitValues,
    createLimitSchema, buildLimitPayload
} from './limitFormSchema';
import { LimitFormFields } from './LimitFormFields';

interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

const CreateLimits: FC<CreateProps> = ({ open, setOpen, rows, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    const [datePatternOptions, setDatePatternOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)

    // Schema con validaciones dinámicas
    const schema = useMemo(() => createLimitSchema(t, rows), [t, rows]);

    const { control, register, reset, handleSubmit, watch, clearErrors, formState: { errors, isDirty } } = useForm<LimitFormValues>({
        defaultValues: defaultLimitValues,
        resolver: zodResolver(schema),
        mode: 'onChange'
    });

    const nameValue = watch('name');
    const descriptionValue = watch('description');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await timekeeperService.date_pattern.getAll();
                setDatePatternOptions(response || []);
            } catch (error) {
                console.error('Error fetching date patterns:', error);
            }
        };

        if (open) {
            fetchData();
            reset(defaultLimitValues);
        }
    }, [open, reset]);

    const changes = () => {
        if (isDirty) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        reset(defaultLimitValues);
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const onSubmit = async (data: LimitFormValues) => {
        setLoading(true);

        try {
            await timekeeperService.limits.create(buildLimitPayload(data));
            enqueueSnackbar(t('accruals.limits.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error('Error creating limit:', error);
            enqueueSnackbar(t('accruals.limits.dialog.alerts.add.error'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.limits.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <LimitFormFields
                    control={control}
                    register={register}
                    errors={errors}
                    clearErrors={clearErrors}
                    loading={loading}
                    nameValue={nameValue}
                    descriptionValue={descriptionValue}
                    datePatternOptions={datePatternOptions}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading || !isDirty} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { CreateLimits };